// src/components/MarkdownEditor.tsx

import React, { useRef, useState, useEffect } from 'react'
import {
  Bold,
  Italic,
  Heading1,
  Heading2,
  Heading3,
  Link as LinkIcon,
  Undo2,
  Redo2,
} from 'lucide-react'
import clsx from 'clsx'

type MarkdownEditorProps = {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  minRows?: number
  maxRows?: number
  autoFocus?: boolean
  disabled?: boolean
}

const TOOLBAR_BUTTONS = [
  {
    label: 'Gras',
    icon: <Bold className="w-4 h-4" />,
    action: 'bold',
    shortcut: 'Ctrl+B',
  },
  {
    label: 'Italique',
    icon: <Italic className="w-4 h-4" />,
    action: 'italic',
    shortcut: 'Ctrl+I',
  },
  {
    label: 'Titre 1',
    icon: <Heading1 className="w-4 h-4" />,
    action: 'h1',
  },
  {
    label: 'Titre 2',
    icon: <Heading2 className="w-4 h-4" />,
    action: 'h2',
  },
  {
    label: 'Titre 3',
    icon: <Heading3 className="w-4 h-4" />,
    action: 'h3',
  },
  {
    label: 'Lien',
    icon: <LinkIcon className="w-4 h-4" />,
    action: 'link',
    shortcut: 'Ctrl+K',
  },
]

export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  placeholder = 'Écrivez votre texte ici…',
  className = '',
  minRows = 6,
  maxRows = 20,
  autoFocus = false,
  disabled = false,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [isLinkModalOpen, setLinkModalOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [undoStack, setUndoStack] = useState<string[]>([])
  const [redoStack, setRedoStack] = useState<string[]>([])

  // Undo/Redo logic
  useEffect(() => {
    setUndoStack([value])
    setRedoStack([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleValueChange = (newValue: string) => {
    setUndoStack((stack) => [...stack, newValue])
    setRedoStack([])
    onChange(newValue)
  }

  const handleUndo = () => {
    if (undoStack.length > 1) {
      const prev = [...undoStack]
      const last = prev.pop()!
      setRedoStack((stack) => [last, ...stack])
      const previousValue = prev[prev.length - 1]
      setUndoStack(prev)
      onChange(previousValue)
    }
  }

  const handleRedo = () => {
    if (redoStack.length > 0) {
      const [next, ...rest] = redoStack
      setUndoStack((stack) => [...stack, next])
      setRedoStack(rest)
      onChange(next)
    }
  }

  // Formatting helpers
  const insertAtSelection = (
    before: string,
    after: string,
    placeholderText: string = '',
    selectPlaceholder = false,
  ) => {
    const textarea = textareaRef.current
    if (!textarea) return

    const { selectionStart, selectionEnd, value: currentValue } = textarea
    const selected = currentValue.slice(selectionStart, selectionEnd) || placeholderText
    const newValue =
      currentValue.slice(0, selectionStart) +
      before +
      selected +
      after +
      currentValue.slice(selectionEnd)
    handleValueChange(newValue)

    // Set selection
    setTimeout(() => {
      if (selectPlaceholder && !selected) {
        textarea.focus()
        textarea.setSelectionRange(
          selectionStart + before.length,
          selectionStart + before.length + placeholderText.length,
        )
      } else {
        textarea.focus()
        textarea.setSelectionRange(
          selectionStart + before.length,
          selectionStart + before.length + selected.length,
        )
      }
    }, 0)
  }

  const handleToolbarAction = (action: string) => {
    switch (action) {
      case 'bold':
        insertAtSelection('**', '**', 'texte en gras', true)
        break
      case 'italic':
        insertAtSelection('*', '*', 'texte en italique', true)
        break
      case 'h1':
        insertAtSelection('\n# ', '', 'Titre 1', true)
        break
      case 'h2':
        insertAtSelection('\n## ', '', 'Titre 2', true)
        break
      case 'h3':
        insertAtSelection('\n### ', '', 'Titre 3', true)
        break
      case 'link':
        setLinkModalOpen(true)
        break
      default:
        break
    }
  }

  const handleLinkInsert = () => {
    if (!linkUrl) return
    insertAtSelection('[', `](${linkUrl})`, 'texte du lien', true)
    setLinkModalOpen(false)
    setLinkUrl('')
  }

  // Keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.metaKey || e.ctrlKey) {
      if (e.key === 'b' || e.key === 'B') {
        e.preventDefault()
        handleToolbarAction('bold')
      }
      if (e.key === 'i' || e.key === 'I') {
        e.preventDefault()
        handleToolbarAction('italic')
      }
      if (e.key === 'k' || e.key === 'K') {
        e.preventDefault()
        handleToolbarAction('link')
      }
      if (e.key === 'z' || e.key === 'Z') {
        e.preventDefault()
        handleUndo()
      }
      if (e.key === 'y' || e.key === 'Y') {
        e.preventDefault()
        handleRedo()
      }
    }
    if (e.key === 'Tab') {
      // Indent with 2 spaces
      e.preventDefault()
      const textarea = textareaRef.current
      if (!textarea) return
      const { selectionStart, selectionEnd, value: currentValue } = textarea
      const newValue =
        currentValue.slice(0, selectionStart) +
        '  ' +
        currentValue.slice(selectionEnd)
      handleValueChange(newValue)
      setTimeout(() => {
        textarea.focus()
        textarea.setSelectionRange(
          selectionStart + 2,
          selectionStart + 2,
        )
      }, 0)
    }
  }

  // Autosize textarea
  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.style.height = 'auto'
    const rows = Math.min(
      Math.max((value || '').split('\n').length, minRows),
      maxRows,
    )

    textarea.rows = rows
    textarea.style.height = textarea.scrollHeight + 'px'
  }, [value, minRows, maxRows])

  // Modal: close on escape
  useEffect(() => {
    if (!isLinkModalOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setLinkModalOpen(false)
        setLinkUrl('')
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isLinkModalOpen])

  return (
    <div className={clsx('w-full', className)}>
      {/* Toolbar */}
      <div className="flex items-center gap-1 mb-2 bg-gray-50 rounded-t border border-b-0 border-gray-200 px-2 py-1">
        {TOOLBAR_BUTTONS.map((btn) => (
          <button
            key={btn.action}
            type="button"
            className="p-1.5 rounded hover:bg-brixel/10 focus:bg-brixel/20 focus:outline-none text-gray-700"
            title={btn.label + (btn.shortcut ? ` (${btn.shortcut})` : '')}
            onClick={() => handleToolbarAction(btn.action)}
            tabIndex={-1}
            disabled={disabled}
          >
            {btn.icon}
          </button>
        ))}
        <div className="flex-1" />
        <button
          type="button"
          className="p-1.5 rounded hover:bg-brixel/10 focus:bg-brixel/20 focus:outline-none text-gray-700"
          title="Annuler (Ctrl+Z)"
          onClick={handleUndo}
          disabled={undoStack.length <= 1 || disabled}
          tabIndex={-1}
        >
          <Undo2 className="w-4 h-4" />
        </button>
        <button
          type="button"
          className="p-1.5 rounded hover:bg-brixel/10 focus:bg-brixel/20 focus:outline-none text-gray-700"
          title="Rétablir (Ctrl+Y)"
          onClick={handleRedo}
          disabled={redoStack.length === 0 || disabled}
          tabIndex={-1}
        >
          <Redo2 className="w-4 h-4" />
        </button>
      </div>
      {/* Textarea */}
      <textarea
        ref={textareaRef}
        className={clsx(
          'block w-full resize-none border border-gray-200 bg-white px-3 py-2 rounded-b font-mono text-sm text-gray-900 focus:ring-2 focus:ring-brixel focus:border-brixel transition min-h-[120px] max-h-[400px] outline-none',
          disabled && 'bg-gray-100 text-gray-400 cursor-not-allowed',
        )}
        value={value}
        onChange={(e) => handleValueChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={minRows}
        autoFocus={autoFocus}
        disabled={disabled}
        spellCheck={true}
        aria-label="Éditeur Markdown"
      />
      {/* Link Modal */}
      {isLinkModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-lg shadow-lg p-6 w-[90vw] max-w-xs flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">
              Adresse du lien (URL)
            </label>
            <input
              type="url"
              className="border border-gray-200 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brixel"
              placeholder="https://exemple.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleLinkInsert()
                }
              }}
            />
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                className="btn flex-1"
                onClick={handleLinkInsert}
                disabled={!linkUrl}
              >
                Insérer
              </button>
              <button
                type="button"
                className="btn-outline flex-1"
                onClick={() => {
                  setLinkModalOpen(false)
                  setLinkUrl('')
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MarkdownEditor